import React from 'react'
// c:\xampp\htdocs\miners-market-main\components\SupplierAnalytics.js

function SupplierManagerModal({ isOpen, onClose, supplier, categories, onSave }) {
    const [formData, setFormData] = React.useState({
        id: supplier?.id || null,
        name: supplier?.name || '',
        text: supplier?.text || '',
        link: supplier?.link || '',
        internal_link: supplier?.internal_link || '',
        display_categories: supplier?.display_categories || [],
        logo: null, // For new file uploads
        existing_logo_path: supplier?.logo || ''
    });
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [error, setError] = React.useState(null);

    if (!isOpen) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleCategoryChange = (slug) => {
        setFormData(prev => {
            const newCategories = prev.display_categories.includes(slug)
                ? prev.display_categories.filter(c => c !== slug)
                : [...prev.display_categories, slug];
            return { ...prev, display_categories: newCategories };
        });
    };

    const handleFileChange = (e) => {
        setFormData(prev => ({ ...prev, logo: e.target.files[0] }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError(null);

        // At least one of Website Link or Internal Link must be provided.
        if (!formData.link.trim() && !formData.internal_link.trim()) {
            setError('Please provide either a Website Link or an Internal Link.');
            setIsSubmitting(false);
            return;
        }

        const submissionData = new FormData();
        submissionData.append('action', formData.id ? 'update' : 'add');
        if (formData.id) {
            submissionData.append('id', formData.id);
        }
        submissionData.append('name', formData.name);
        submissionData.append('text', formData.text);
        submissionData.append('link', formData.link);
        submissionData.append('internal_link', formData.internal_link);
        submissionData.append('display_categories', JSON.stringify(formData.display_categories));
        submissionData.append('existing_logo_path', formData.existing_logo_path);
        if (formData.logo) {
            submissionData.append('logo', formData.logo);
        }

        try {
            const response = await fetch('/api/manage_supplier.php', {
                method: 'POST',
                credentials: 'include',
                body: submissionData
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.message || 'Failed to save supplier.');
            onSave(); // Trigger refresh
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const mainCategories = categories.filter(c => !c.parent_id);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-8 max-w-2xl w-full max-h-[95vh] overflow-y-auto">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">{supplier ? 'Edit' : 'Add'} Supplier</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Supplier Name*</label>
                        <input type="text" name="name" value={formData.name} onChange={handleChange} required className="mt-1 block w-full border-gray-300 rounded-md shadow-sm" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Description Text*</label>
                        <textarea name="text" value={formData.text} onChange={handleChange} required rows="3" className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"></textarea>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Website Link <span className="text-gray-400 font-normal">(optional)</span></label>
                        <input type="url" name="link" value={formData.link} onChange={handleChange} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm" placeholder="https://example.com" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Internal Link <span className="text-gray-400 font-normal">(optional)</span></label>
                        <input type="text" name="internal_link" value={formData.internal_link} onChange={handleChange} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm" placeholder="/equipment, /miscellaneous, /components-and-spares, /rebuilds, /rentals, /commercial, /search" />
                        <p className="mt-1 text-xs text-gray-500">Use an internal app route (e.g. /equipment or /miscellaneous?category=diesel) instead of an external website. At least one of Website or Internal Link is required.</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Logo</label>
                        <input type="file" name="logo" onChange={handleFileChange} accept="image/*" className="mt-1 block w-full text-sm" />
                        {formData.existing_logo_path && <img src={formData.existing_logo_path} alt="Current logo" className="mt-2 h-16 w-auto rounded" />}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Display Categories</label>
                        <div className="space-y-2 p-3 border rounded-md max-h-60 overflow-y-auto">
                            <div className="flex items-center">
                                <input type="checkbox" id="cat-all" checked={formData.display_categories.includes('all')} onChange={() => handleCategoryChange('all')} className="h-4 w-4" />
                                <label htmlFor="cat-all" className="ml-2 text-sm font-bold">Show on ALL Marketplace Pages</label>
                            </div>
                            <div className="flex items-center">
                                <input type="checkbox" id="cat-all-products" checked={formData.display_categories.includes('all-products-only')} onChange={() => handleCategoryChange('all-products-only')} className="h-4 w-4" />
                                <label htmlFor="cat-all-products" className="ml-2 text-sm font-bold">Show only on main "All Products" page</label>
                            </div>
                            <hr className="my-2"/>
                            {mainCategories.map(cat => (
                                <div key={cat.slug}>
                                    <div className="flex items-center">
                                        <input type="checkbox" id={`cat-${cat.slug}`} checked={formData.display_categories.includes(cat.slug)} onChange={() => handleCategoryChange(cat.slug)} className="h-4 w-4" />
                                        <label htmlFor={`cat-${cat.slug}`} className="ml-2 text-sm">{cat.name}</label>
                                    </div>
                                    {(cat.slug === 'miscellaneous' || cat.slug === 'miscellaneous-items') && (
                                        <div className="flex items-center ml-6 mt-1">
                                            <input type="checkbox" id="cat-diesel" checked={formData.display_categories.includes('diesel')} onChange={() => handleCategoryChange('diesel')} className="h-4 w-4" />
                                            <label htmlFor="cat-diesel" className="ml-2 text-sm">Diesel</label>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                    {error && <div className="text-red-500 text-sm">{error}</div>}
                    <div className="flex justify-end space-x-4 pt-4">
                        <button type="button" onClick={onClose} className="bg-gray-200 text-gray-800 py-2 px-4 rounded-lg">Cancel</button>
                        <button type="submit" disabled={isSubmitting} className="primary-gradient text-white py-2 px-4 rounded-lg disabled:opacity-50">
                            {isSubmitting ? 'Saving...' : 'Save Supplier'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default function SupplierAnalytics() {
    const [analytics, setAnalytics] = React.useState([]);
    const [suppliers, setSuppliers] = React.useState([]);
    const [categories, setCategories] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [showModal, setShowModal] = React.useState(false);
    const [supplierToEdit, setSupplierToEdit] = React.useState(null);
    const [actionMessage, setActionMessage] = React.useState(null);
    const [isDownloading, setIsDownloading] = React.useState(null); // Tracks which supplier PDF is being generated

    // --- NEW: State for date filtering ---
    const today = new Date();
    const thirtyDaysAgo = new Date(new Date().setDate(today.getDate() - 30));
    const [startDate, setStartDate] = React.useState(thirtyDaysAgo.toISOString().split('T')[0]);
    const [endDate, setEndDate] = React.useState(today.toISOString().split('T')[0]);

    React.useEffect(() => {
        fetchData();
    }, []); // Initial fetch on mount

    // --- NEW: Function to fetch data based on date range ---
    const fetchData = async (start = startDate, end = endDate) => {
        try {
            setLoading(true);
            setError(null);
            
            // Construct the URL with date parameters for analytics
            const analyticsUrl = `/api/admin_get_supplier_analytics.php?start_date=${start}&end_date=${end}`;

            const [analyticsRes, suppliersRes, categoriesRes] = await Promise.all([
                fetch(analyticsUrl, { credentials: 'include' }),
                fetch('/api/get_suppliers.php', { credentials: 'include' }),
                fetch('/api/categories.php', { credentials: 'include' })
            ]);

            if (!analyticsRes.ok) throw new Error('Failed to fetch analytics.');
            if (!suppliersRes.ok) throw new Error('Failed to fetch suppliers.');
            if (!categoriesRes.ok) throw new Error('Failed to fetch categories.');

            const analyticsData = await analyticsRes.json();
            const suppliersData = await suppliersRes.json();
            const categoriesData = await categoriesRes.json();

            setAnalytics(analyticsData);
            setSuppliers(suppliersData);
            setCategories(categoriesData);

        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = () => {
        // Refetch suppliers after saving
        fetch('/api/get_suppliers.php', { credentials: 'include' })
            .then(res => res.json())
            .then(data => setSuppliers(data))
            .catch(err => setError(err.message));
    };

    const handleAddClick = () => {
        setSupplierToEdit(null);
        setShowModal(true);
    };

    const handleEditClick = (supplier) => {
        setSupplierToEdit(supplier);
        setShowModal(true);
    };

    const handleDeleteClick = async (supplierId) => {
        if (!window.confirm("Are you sure you want to delete this supplier? This cannot be undone.")) {
            return;
        }
        setActionMessage(null);
        const formData = new FormData();
        formData.append('action', 'delete');
        formData.append('id', supplierId);

        try {
            const response = await fetch('/api/manage_supplier.php', {
                method: 'POST',
                credentials: 'include',
                body: formData
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.message);
            setActionMessage({ type: 'success', text: result.message });
            handleSave(); // Refresh list
        } catch (err) {
            setActionMessage({ type: 'error', text: err.message });
        }
    };

    const handleToggleStatus = async (supplierId, currentStatus) => {
        const newStatus = currentStatus === 'active' ? 'paused' : 'active';
        setActionMessage(null);

        try {
            const response = await fetch('/api/update_supplier_status.php', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: supplierId, status: newStatus })
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.message);
            
            setActionMessage({ type: 'success', text: result.message });
            // Refresh the supplier list to show the new status
            const suppliersRes = await fetch('/api/get_suppliers.php', { credentials: 'include' });
            const suppliersData = await suppliersRes.json();
            setSuppliers(suppliersData);
        } catch (err) {
            setActionMessage({ type: 'error', text: err.message });
        }
    };

    const handleDownloadPdf = async (supplierName) => {
        setIsDownloading(supplierName);
        setActionMessage(null);
        try {
            // 1. Fetch detailed data for the report
            const reportUrl = `/api/get_supplier_report_data.php?supplier_name=${encodeURIComponent(supplierName)}&start_date=${startDate}&end_date=${endDate}`;
            const response = await fetch(reportUrl, { credentials: 'include' });
            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.message || 'Failed to fetch report data.');
            }
            const reportData = await response.json();

            // 2. Initialize jsPDF
            // This uses the jsPDF library (MIT license) to create the document.
            const { jsPDF } = window.jspdf;
            const doc = new jsPDF();

            // 3. Add Logos
            const minersMarketLogo = new Image();
            minersMarketLogo.src = 'images/logo/MM Logo.jpg';
            // Wait for the image to load before adding it to the PDF
            await new Promise(resolve => { minersMarketLogo.onload = resolve; });
            doc.addImage(minersMarketLogo, 'JPEG', 15, 15, 30, 30);

            if (reportData.supplier_logo) {
                const supplierLogo = new Image();
                supplierLogo.crossOrigin = 'Anonymous'; // Important for external images
                supplierLogo.src = reportData.supplier_logo;
                await new Promise(resolve => supplierLogo.onload = resolve);
                doc.addImage(supplierLogo, 'PNG', doc.internal.pageSize.getWidth() - 45, 15, 30, 30);
            }

            // 4. Add Title
            doc.setFontSize(22);
            doc.setFont('helvetica', 'bold');
            doc.text('Supplier Analytics Report', doc.internal.pageSize.getWidth() / 2, 30, { align: 'center' });
            doc.setFontSize(16);
            doc.setFont('helvetica', 'normal');
            doc.text(supplierName, doc.internal.pageSize.getWidth() / 2, 40, { align: 'center' });
            doc.setFontSize(10);
            doc.setTextColor(150, 150, 150); // Use RGB for maximum compatibility
            doc.text(`Report for ${startDate} to ${endDate}`, doc.internal.pageSize.getWidth() / 2, 45, { align: 'center' });

            // 5. Add Data Tables
            // This uses the jspdf-autotable plugin (MIT license) to generate tables.
            const tableStyles = {
                headStyles: { fillColor: [22, 160, 133] },
                startY: 60,
                // --- FIX: Explicitly set text color for the table ---
                // This overrides any corrupted internal state and prevents the 'getTextColor' error.
                styles: { textColor: [33, 33, 33] },
            };
            const columns = ["Metric", "Views", "Clicks", "Unique Clicks"];

            // Filtered Stats Table
            const filteredData = reportData.filtered_stats;
            const tableBody = [
                [`${startDate} to ${endDate}`, filteredData.view_count, filteredData.total_clicks, filteredData.unique_users]
            ];
            // Call autoTable as a method on the doc instance.
            // The 'html' property is added to force a more stable rendering path in the library,
            // which prevents the 'getTextColor' error.
            doc.autoTable({
                head: [columns],
                body: tableBody,
                ...tableStyles
            });

            // 6. Add Footer
            const pageCount = doc.internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.setFontSize(8);
                doc.setTextColor(100, 100, 100); // Use RGB for maximum compatibility
                doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.getWidth() / 2, 287, { align: 'center' });
                doc.text('Miners Market | Confidential', 15, 287);
            }

            // 7. Save the PDF
            doc.save(`MinersMarket_Analytics_${supplierName.replace(/ /g, '_')}.pdf`);

        } catch (err) {
            setActionMessage({ type: 'error', text: err.message });
        } finally {
            setIsDownloading(null);
        }
    };

    // --- NEW: Merged Analytics Logic ---
    // This ensures that all suppliers from the management list appear in the analytics table,
    // even if they have no analytics data yet.
    const mergedAnalytics = React.useMemo(() => {
        if (loading) return [];
        // Create a map of analytics data for quick lookups. Also include supplier status.
        const analyticsMap = new Map(analytics.map(item => [item.supplier_name, item]));

        // Map over the definitive list of suppliers.
        return suppliers.map(supplier => {
            const analyticData = analyticsMap.get(supplier.name);
            return { ...analyticData, supplier_name: supplier.name };
        });
    }, [suppliers, analytics, loading]);

    return (
        <>
            <div className="bg-white rounded-xl shadow-lg p-6 mt-8">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">Supplier Management</h2>
                    <button onClick={handleAddClick} className="primary-gradient text-white font-bold py-2 px-4 rounded-lg hover:opacity-90">
                        <i className="fas fa-plus mr-2"></i>Add Supplier
                    </button>
                </div>

                {actionMessage && <div className={`p-3 rounded-lg text-sm mb-4 ${actionMessage.type === 'error' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>{actionMessage.text}</div>}

                {loading && <div className="text-center py-10"><i className="fas fa-spinner fa-spin text-3xl text-blue-400"></i></div>}
                {error && <div className="text-center py-10 text-red-500">Error: {error}</div>}

                {!loading && !error && suppliers.length > 0 && (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Logo</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Link</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {suppliers.map(s => (
                                    <tr key={s.id}>
                                        <td className={`px-6 py-4 ${s.status === 'paused' ? 'opacity-50' : ''}`}><img src={s.logo} alt={s.name} className="h-10 w-auto rounded" /></td>
                                        <td className={`px-6 py-4 font-medium ${s.status === 'paused' ? 'text-gray-400' : 'text-gray-900'}`}>{s.name}</td>
                                        <td className={`px-6 py-4 ${s.status === 'paused' ? 'opacity-50' : ''}`}>
                                            <a href={s.link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate block max-w-xs">{s.link}</a>
                                            {s.internal_link && <span className="block text-xs text-gray-500 mt-1 truncate max-w-xs"><i className="fas fa-link mr-1"></i>Internal: {s.internal_link}</span>}
                                        </td>
                                        <td className="px-6 py-4 text-right space-x-2">
                                            <button onClick={() => handleToggleStatus(s.id, s.status)} className={`font-medium ${s.status === 'active' ? 'text-yellow-600 hover:text-yellow-900' : 'text-green-600 hover:text-green-900'}`}>
                                                {s.status === 'active' ? 'Pause' : 'Resume'}
                                            </button>
                                            <button onClick={() => handleEditClick(s)} className="text-blue-600 hover:text-blue-900">Edit</button>
                                            <button onClick={() => handleDeleteClick(s.id)} className="text-red-600 hover:text-red-900">Delete</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 mt-8">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                    <h2 className="text-2xl font-bold text-gray-900">Supplier Click Analytics</h2>
                    <div className="flex items-center gap-2">
                        <div>
                            <label htmlFor="start-date" className="text-sm font-medium text-gray-700">From:</label>
                            <input type="date" id="start-date" value={startDate} onChange={e => setStartDate(e.target.value)} className="ml-1 border-gray-300 rounded-md shadow-sm text-sm"/>
                        </div>
                        <div>
                            <label htmlFor="end-date" className="text-sm font-medium text-gray-700">To:</label>
                            <input type="date" id="end-date" value={endDate} onChange={e => setEndDate(e.target.value)} className="ml-1 border-gray-300 rounded-md shadow-sm text-sm"/>
                        </div>
                        <button onClick={() => fetchData(startDate, endDate)} className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 text-sm">
                            <i className="fas fa-filter mr-1"></i> Filter
                        </button>
                    </div>
                </div>

                {!loading && !error && mergedAnalytics.length > 0 && (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Supplier</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Views</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Clicks</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unique Clicks</th>
                                </tr>
                                <th scope="col" className="relative px-6 py-3"><span className="sr-only">Download</span></th>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {mergedAnalytics.map(item => (
                                    <tr key={item.supplier_name}>
                                        <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm font-medium text-gray-900">{item.supplier_name}</div></td>
                                        <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-gray-900">{item.total_views || 0}</div></td>
                                        <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-gray-900">{item.total_clicks || 0}</div></td>
                                        <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-gray-900">{item.unique_users || 0}</div></td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right">
                                            <button onClick={() => handleDownloadPdf(item.supplier_name)} disabled={isDownloading === item.supplier_name} className="text-blue-600 hover:text-blue-900 disabled:opacity-50 disabled:cursor-wait">
                                                {isDownloading === item.supplier_name 
                                                    ? <><i className="fas fa-spinner fa-spin mr-1"></i>Generating...</> 
                                                    : <><i className="fas fa-file-pdf mr-1"></i>Download</>
                                                }
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <p className="text-xs text-gray-500 mt-4">
                            Note: "Unique Clicks" only counts clicks from users who were logged in at the time.
                        </p>
                    </div>
                )}
            </div>
            {showModal && <SupplierManagerModal isOpen={showModal} onClose={() => setShowModal(false)} supplier={supplierToEdit} categories={categories} onSave={handleSave} />}
        </>
    );
}
